import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Play, Pause, Maximize, Palette, Paintbrush, Monitor, Smartphone, Grid, Wind, Moon, Sun, Shuffle, Infinity as InfinityIcon, Layers } from 'lucide-react';

/* — MANDALA ICON — */
const MandalaIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <line x1="12" y1="2" x2="12" y2="7" /><line x1="12" y1="17" x2="12" y2="22" />
    <line x1="2" y1="12" x2="7" y2="12" /><line x1="17" y1="12" x2="22" y2="12" />
    <line x1="5.05" y1="5.05" x2="8.52" y2="8.52" /><line x1="15.48" y1="15.48" x2="18.95" y2="18.95" />
    <line x1="18.95" y1="5.05" x2="15.48" y2="8.52" /><line x1="8.52" y1="15.48" x2="5.05" y2="18.95" />
  </svg>
);

/* — SIMPLEX NOISE ENGINE (Intouché) — */
const createNoise3D = () => {
  const perm = new Uint8Array(512);
  const p = new Uint8Array(256).map((_, i) => i);
  for (let i = 0; i < 256; i++) {
    const r = (Math.random() * (256 - i)) | 0;
    const t = p[i]; p[i] = p[i + r]; p[i + r] = t;
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
  const grad3 = [[1,1,0],[-1,1,0],[1,-1,0],[-1,-1,0],[1,0,1],[-1,0,1],[1,0,-1],[-1,0,-1],[0,1,1],[0,-1,1],[0,1,-1],[0,-1,-1]];
  return (x, y, z) => {
    let n0, n1, n2, n3;
    const F3 = 1.0/3.0, S3 = 1.0/6.0;
    const s = (x+y+z)*F3;
    const i = Math.floor(x+s), j = Math.floor(y+s), k = Math.floor(z+s);
    const t = (i+j+k)*S3;
    const X0 = i-t, Y0 = j-t, Z0 = k-t;
    const x0 = x-X0, y0 = y-Y0, z0 = z-Z0;
    let i1, j1, k1, i2, j2, k2;
    if(x0>=y0){if(y0>=z0){i1=1;j1=0;k1=0;i2=1;j2=1;k2=0;}else if(x0>=z0){i1=1;j1=0;k1=0;i2=1;j2=0;k2=1;}else{i1=0;j1=0;k1=1;i2=1;j2=0;k2=1;}}
    else{if(y0<z0){i1=0;j1=0;k1=1;i2=0;j2=1;k2=1;}else if(x0<z0){i1=0;j1=1;k1=0;i2=0;j2=1;k2=1;}else{i1=0;j1=1;k1=0;i2=1;j2=1;k2=0;}}
    const x1=x0-i1+S3, y1=y0-j1+S3, z1=z0-k1+S3;
    const x2=x0-i2+2.0*S3, y2=y0-j2+2.0*S3, z2=z0-k2+2.0*S3;
    const x3=x0-1.0+3.0*S3, y3=y0-1.0+3.0*S3, z3=z0-1.0+3.0*S3;
    const ii=i&255, jj=j&255, kk=k&255;
    const gi0 = perm[ii+perm[jj+perm[kk]]] % 12;
    const gi1 = perm[ii+i1+perm[jj+j1+perm[kk+k1]]] % 12;
    const gi2 = perm[ii+i2+perm[jj+j2+perm[kk+k2]]] % 12;
    const gi3 = perm[ii+1+perm[jj+1+perm[kk+1]]] % 12;
    let t0 = 0.6-x0*x0-y0*y0-z0*z0; if(t0<0)n0=0.0;else{t0*=t0;n0=t0*t0*(grad3[gi0][0]*x0+grad3[gi0][1]*y0+grad3[gi0][2]*z0);}
    let t1 = 0.6-x1*x1-y1*y1-z1*z1; if(t1<0)n1=0.0;else{t1*=t1;n1=t1*t1*(grad3[gi1][0]*x1+grad3[gi1][1]*y1+grad3[gi1][2]*z1);}
    let t2 = 0.6-x2*x2-y2*y2-z2*z2; if(t2<0)n2=0.0;else{t2*=t2;n2=t2*t2*(grad3[gi2][0]*x2+grad3[gi2][1]*y2+grad3[gi2][2]*z2);}
    let t3 = 0.6-x3*x3-y3*y3-z3*z3; if(t3<0)n3=0.0;else{t3*=t3;n3=t3*t3*(grad3[gi3][0]*x3+grad3[gi3][1]*y3+grad3[gi3][2]*z3);}
    return 32.0*(n0+n1+n2+n3);
  };
};

const noise = createNoise3D();

/* — COLOR CONVERSION UTILS (for WebGL shader) — */
const hslToRgb = (h, s, l) => {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) return [l, l, l];
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1/3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1/3)];
};
const hexToRgb = (hex) => {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? [parseInt(r[1], 16) / 255, parseInt(r[2], 16) / 255, parseInt(r[3], 16) / 255] : [0, 0, 0];
};

/* — GLOW FLUID SIM —
   Champ de couleurs basse résolution advecté par un champ de vitesse
   incompressible (curl noise), projeté en kaléidoscope pour la symétrie. */
const AQ_SIM = 200;   // résolution de la grille de pigment
const AQ_VEL = 34;    // résolution de la grille de vitesse (curl noise)

/* — ROSACE GLSL SHADERS — */
const ROSACE_VS = `attribute vec4 aVertexPosition; void main() { gl_Position = aVertexPosition; }`;
const ROSACE_FS = `
precision highp float;
uniform vec2 u_resolution;
uniform float u_time;
uniform vec3 u_color0, u_color1, u_color2, u_color3;
uniform float u_lightMode;
uniform vec3 u_bgColor;

mat2 rot(float a) { float s = sin(a), c = cos(a); return mat2(c, -s, s, c); }

vec3 getPalette(float t) {
  t = fract(t) * 4.0;
  if (t < 1.0) return mix(u_color0, u_color1, t);
  if (t < 2.0) return mix(u_color1, u_color2, t - 1.0);
  if (t < 3.0) return mix(u_color2, u_color3, t - 2.0);
  return mix(u_color3, u_color0, t - 3.0);
}

void main() {
  vec2 uv = (gl_FragCoord.xy * 2.0 - u_resolution.xy) / min(u_resolution.y, u_resolution.x);
  uv *= 1.1;
  vec2 uvOrigin = uv;

  float t = u_time * 0.2;
  for (float i = 0.0; i < 3.0; i++) {
    uv = abs(uv);
    uv *= rot(t * 0.5 + i);
    uv -= 0.2 * sin(t + i);
  }

  float r = length(uvOrigin);
  float angle = atan(uvOrigin.y, uvOrigin.x);
  float masterMask = smoothstep(1.0, 0.98, r);

  if (r > 1.05) { gl_FragColor = vec4(u_bgColor, 1.0); return; }

  float rGrid = fract(r * 140.0);
  float aGrid = fract((angle / 6.28318 + 0.5) * 160.0);
  float distToPoint = length(vec2(rGrid, aGrid) - 0.5);

  float pattern = 0.0;
  for (float i = 1.0; i <= 4.0; i++) {
    float radial = sin(r * (10.0 * i) - u_time * 1.5);
    float shape = cos(uv.x * (5.0 * i) + u_time) * sin(uv.y * (5.0 * i) - u_time);
    pattern += abs(radial * shape) * (1.0 / i);
  }
  pattern += 0.2 * sin(r * 20.0 - u_time * 3.0 + angle * 4.0);
  pattern = abs(pattern);

  float colorIdx = r * 0.6 + pattern * 0.2 - u_time * 0.1;
  vec3 baseCol = getPalette(colorIdx);

  float pointGlow = smoothstep(0.5, 0.0, distToPoint);
  float baseGlow = 0.4 + 0.6 * pointGlow;
  float intensity = (0.2 + pattern * 1.8) * baseGlow;

  vec3 finalColor;
  if (u_lightMode > 0.5) {
    vec3 tint = baseCol * intensity;
    finalColor = u_bgColor * (1.0 - tint * 0.6);
    float core = 0.015 / (r + 0.015);
    finalColor -= baseCol * core * baseGlow * 0.25;
    finalColor = clamp(finalColor, 0.0, 1.0);
  } else {
    finalColor = baseCol * intensity;
    float core = 0.02 / (r + 0.015);
    vec3 coreColor = mix(u_color0, vec3(0.2, 0.8, 1.0), 0.5) * core * baseGlow;
    finalColor += coreColor;
  }

  finalColor = mix(u_bgColor, finalColor, masterMask);
  float sparkle = 1.0 + 0.08 * sin(r * 600.0 + angle * 300.0 + u_time * 15.0);
  gl_FragColor = vec4(finalColor * sparkle, 1.0);
}
`;

/* — DATA: PALETTES (Intouchées) — */
const WATERCOLOR_PALETTES = [
  { name: "Lagon Profond", bg: "#f0f8ff", colors: [[190, 90, 45], [210, 85, 50], [170, 95, 40], [220, 80, 55]] },
  { name: "Encre Zen", bg: "#ffffff", colors: [[0, 0, 0], [0, 0, 20], [0, 0, 40], [200, 5, 30]] },
  { name: "Feu Sacré", bg: "#fffbf0", colors: [[350, 85, 55], [20, 90, 60], [45, 100, 50], [300, 70, 45]] },
  { name: "Encre Royale", bg: "#fdfdfd", colors: [[220, 90, 20], [340, 80, 40], [260, 85, 30], [200, 90, 25]] },
  { name: "Jungle", bg: "#f0f5f0", colors: [[140, 80, 35], [160, 90, 40], [90, 85, 45], [120, 70, 30]] },
  { name: "Lotus", bg: "#faf5f8", colors: [[310, 80, 45], [280, 70, 55], [340, 90, 60], [260, 60, 50]] },
  { name: "Épices", bg: "#fcf5e5", colors: [[30, 90, 55], [10, 85, 50], [45, 100, 45], [0, 80, 40]] },
  { name: "Chakras", bg: "#ffffff", colors: [[0, 90, 50], [30, 90, 50], [60, 90, 45], [120, 90, 40], [240, 90, 50], [280, 90, 50]] },
];

const COSMIC_PALETTES = [
  { name: "Nébuleuse", bg: "#050508", colors: [[260, 100, 60], [200, 100, 50], [320, 100, 65], [180, 90, 55]] },
  { name: "Aurore Boréale", bg: "#000502", colors: [[160, 100, 50], [120, 90, 60], [190, 100, 55], [280, 80, 40]] },
  { name: "Magma Noir", bg: "#0a0202", colors: [[10, 100, 55], [30, 100, 50], [0, 90, 45], [340, 100, 60]] },
  { name: "Abysses", bg: "#000208", colors: [[220, 90, 40], [240, 80, 50], [190, 100, 30], [270, 70, 60]] },
  { name: "Cyberpunk", bg: "#080008", colors: [[300, 100, 50], [180, 100, 50], [60, 100, 50], [330, 100, 60]] },
  { name: "Vide Quantique", bg: "#000000", colors: [[0, 0, 100], [0, 0, 80], [240, 50, 70], [0, 0, 60]] }, 
];

/* — MODES GÉOMÉTRIQUES ENRICHIS — */
const GENERATION_MODES = {
  // Modes originaux
  SPIRAL_FIBONACCI: 0,
  RADIAL_BURST: 1,
  LISSAJOUS: 2,
  POLAR_ROSE: 3,
  EPICYCLOID: 4,
  HYPOCYCLOID: 5,
  CARDIOID: 6,
  LIMACON: 7,
  ASTROID: 8,
  DELTOID: 9,
  NEPHROID: 10,
  HYPOTROCHOID: 11,
  // Nouveaux modes
  SPIROGRAPH: 12,
  FERMAT_SPIRAL: 13,
  GOLDEN_SPIRAL: 14,
  SUPERELLIPSE: 15,
  BUTTERFLY_CURVE: 16,
  MAURER_ROSE: 17,
  HARMONOGRAPH: 18,
  RHODONEA: 19,
  EPITROCHOID: 20,
  INVOLUTE: 21,
  LOGARITHMIC_SPIRAL: 22,
  COTES_SPIRAL: 23,
  TALBOT_CURVE: 24,
  BICORN: 25,
  FOLIUM: 26,
  LEMNISCATE: 27,
  TREFOIL: 28,
  QUADRIFOLIUM: 29,
  RANUNCULOID: 30,
  COCHLEOID: 31,
};

const App = () => {
  const canvasRef = useRef(null);
  const frameId = useRef(0);
  
  // States
  const [displayMode, setDisplayMode] = useState('white');
  const [isPlaying, setIsPlaying] = useState(true);
  const [showUI, setShowUI] = useState(false);
  const [orientation, setOrientation] = useState("landscape");
  
  // New States for Palette Menu
  const [showPaletteMenu, setShowPaletteMenu] = useState(false);
  const [paletteMode, setPaletteMode] = useState('fixed'); // 'fixed', 'auto', 'fusion'
  const [currentPaletteName, setCurrentPaletteName] = useState("");
  
  // Mandala mode
  const [mandalaMode, setMandalaMode] = useState('morphogenesis');
  const [showMandalaMenu, setShowMandalaMenu] = useState(false);
  const isMouseDown = useRef(false);

  // Bubbles engine refs
  const bubblesParticles = useRef([]);
  const bubblesState = useRef({
    currentSpeed: 0.5,
    targetSpeed: 0.5,
    spawnAccumulator: 0,
  });

  // Hyperspace engine refs (new simplified engine)
  const hyperspaceParticles = useRef([]);

  // Glow engine refs (simulation fluide)
  const glowSim = useRef(null);

  // Rosace (WebGL) engine refs
  const glCanvasRef = useRef(null);
  const glRef = useRef(null);
  const glProgramRef = useRef(null);
  const glUniformsRef = useRef(null);
  const glBufferRef = useRef(null);
  const rosaceVirtualTime = useRef(0);
  const lastFrameTime = useRef(performance.now());

  // Global speed control (unified for all mandalas)
  const globalSpeed = useRef(1.0);
  const targetGlobalSpeed = useRef(1.0);
  const lastTouchY = useRef(null);

  // Timer for Auto Mode
  const autoSwitchTime = useRef(0);

  const dna = useRef({
    time: 0,
    speed: 0.00001, 
    zoom: 0.001,
    symmetry: 8,
    paletteIndex: 0,
    brushSizeBase: 12,
    wetness: 0.012,
    washCycle: 0,
    organicPhase: 0,
    structureComplexity: 0,
    morphMode: 0,
    generationMode: GENERATION_MODES.SPIRAL_FIBONACCI,
    modePhase: 0,
    geometricFreq1: 3,
    geometricFreq2: 5,
    geometricFreq3: 7, // Nouveau paramètre pour courbes complexes
    geometricAmp: 0.3,
    geometricAmp2: 0.2, // Amplitude secondaire
    radialLayers: 3,
    spiralTightness: 0.1,
    waveComplexity: 2,
    turbulenceScale: 1,
    centerWarp: 0,
    // Nouveaux paramètres pour structures avancées
    harmonicDamping: 0.995,
    phaseShift: 0,
    modulationDepth: 0.5,
    eccentricity: 0.5,
    petalCount: 5,
    maurerDegree: 71, // Nombre premier pour Maurer Rose
  });

  const brushes = useRef([]);
  const canvasSize = useRef({ w: 0, h: 0, minDim: 0 });

  const getPalettes = useCallback(() => {
    return displayMode === 'white' ? WATERCOLOR_PALETTES : COSMIC_PALETTES;
  }, [displayMode]);

  // Helper pour récupérer la palette active selon le mode (Fixed/Auto/Fusion)
  const getActivePaletteData = useCallback(() => {
    const palettes = getPalettes();
    
    if (paletteMode === 'fusion') {
      // MODE FUSION : On combine toutes les couleurs
      const allColors = palettes.flatMap(p => p.colors);
      return { 
        name: "Fusion Totale", 
        bg: palettes[0].bg, // On garde le fond du premier pour rester cohérent avec le mode White/Black
        colors: allColors 
      };
    }
    
    // MODES FIXED & AUTO : On utilise l'index courant
    // Sécurité d'index
    if (dna.current.paletteIndex >= palettes.length) dna.current.paletteIndex = 0;
    return palettes[dna.current.paletteIndex];
  }, [getPalettes, paletteMode]);

  const handleResize = useCallback(() => {
    // Utilise visualViewport (mobile-safe) pour obtenir la vraie taille dispo
    const vv = window.visualViewport;
    const w = vv ? vv.width : window.innerWidth;
    const h = vv ? vv.height : window.innerHeight;
    const newOrientation = w > h ? "landscape" : "portrait";
    setOrientation(newOrientation);
    canvasSize.current = { w, h, minDim: Math.min(w, h) };

    if (canvasRef.current) {
      canvasRef.current.width = w;
      canvasRef.current.height = h;
      canvasRef.current.style.width = w + 'px';
      canvasRef.current.style.height = h + 'px';
      const ctx = canvasRef.current.getContext('2d');
      const pal = getActivePaletteData();
      ctx.fillStyle = pal.bg;
      ctx.fillRect(0, 0, w, h);
      initBrushes(w, h);
    }
    if (glCanvasRef.current) {
      glCanvasRef.current.width = w;
      glCanvasRef.current.height = h;
      glCanvasRef.current.style.width = w + 'px';
      glCanvasRef.current.style.height = h + 'px';
      if (glRef.current) glRef.current.viewport(0, 0, w, h);
    }
  }, [getActivePaletteData]); // initBrushes dep is added below

  const generateGeometricPosition = (index, total, w, h, minDim, d) => {
    const t = index / total;
    const centerX = w / 2;
    const centerY = h / 2;
    let x = centerX, y = centerY;
    const baseRadius = minDim * 0.4;
    
    switch(d.generationMode) {
      case GENERATION_MODES.SPIRAL_FIBONACCI: {
        const angle = t * Math.PI * 2 * d.geometricFreq1 + d.modePhase;
        const r = baseRadius * Math.sqrt(t) * (1 + d.spiralTightness);
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      case GENERATION_MODES.RADIAL_BURST: {
        const layer = Math.floor(t * d.radialLayers);
        const angleInLayer = (t * d.radialLayers - layer) * Math.PI * 2;
        const r = baseRadius * (layer + 1) / d.radialLayers;
        x = centerX + r * Math.cos(angleInLayer + d.modePhase);
        y = centerY + r * Math.sin(angleInLayer + d.modePhase);
        break;
      }
      case GENERATION_MODES.LISSAJOUS: {
        const angle = t * Math.PI * 2;
        x = centerX + baseRadius * Math.sin(d.geometricFreq1 * angle + d.modePhase);
        y = centerY + baseRadius * Math.sin(d.geometricFreq2 * angle);
        break;
      }
      case GENERATION_MODES.POLAR_ROSE: {
        const angle = t * Math.PI * 2 * d.geometricFreq2;
        const r = baseRadius * Math.abs(Math.cos(d.geometricFreq1 * angle + d.modePhase));
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      case GENERATION_MODES.EPICYCLOID: {
        const angle = t * Math.PI * 2;
        const R = baseRadius * 0.6;
        const r = R / d.geometricFreq1;
        const ratio = (R + r) / r;
        x = centerX + (R + r) * Math.cos(angle) - r * Math.cos(ratio * angle + d.modePhase);
        y = centerY + (R + r) * Math.sin(angle) - r * Math.sin(ratio * angle + d.modePhase);
        break;
      }
      case GENERATION_MODES.HYPOCYCLOID: {
        const angle = t * Math.PI * 2;
        const R = baseRadius * 0.7;
        const r = R / d.geometricFreq1;
        const ratio = (R - r) / r;
        x = centerX + (R - r) * Math.cos(angle) + r * Math.cos(ratio * angle + d.modePhase);
        y = centerY + (R - r) * Math.sin(angle) - r * Math.sin(ratio * angle + d.modePhase);
        break;
      }
      case GENERATION_MODES.CARDIOID: {
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.5;
        const r = 2 * a * (1 - Math.cos(angle));
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      case GENERATION_MODES.LIMACON: {
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.4;
        const b = baseRadius * 0.3;
        const r = a + b * Math.cos(angle);
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      case GENERATION_MODES.ASTROID: {
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.6;
        x = centerX + a * Math.pow(Math.cos(angle), 3);
        y = centerY + a * Math.pow(Math.sin(angle), 3);
        break;
      }
      case GENERATION_MODES.DELTOID: {
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.5;
        x = centerX + 2 * a * Math.cos(angle) + a * Math.cos(2 * angle);
        y = centerY + 2 * a * Math.sin(angle) - a * Math.sin(2 * angle);
        break;
      }
      case GENERATION_MODES.NEPHROID: {
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.4;
        x = centerX + a * (3 * Math.cos(angle) - Math.cos(3 * angle));
        y = centerY + a * (3 * Math.sin(angle) - Math.sin(3 * angle));
        break;
      }
      case GENERATION_MODES.HYPOTROCHOID: {
        const angle = t * Math.PI * 2;
        const R = baseRadius * 0.6;
        const r = R / d.geometricFreq1;
        const d_dist = r * d.geometricAmp * 2;
        const ratio = (R - r) / r;
        x = centerX + (R - r) * Math.cos(angle) + d_dist * Math.cos(ratio * angle + d.modePhase);
        y = centerY + (R - r) * Math.sin(angle) - d_dist * Math.sin(ratio * angle + d.modePhase);
        break;
      }
      
      // === NOUVEAUX MODES ===
      
      case GENERATION_MODES.SPIROGRAPH: {
        // Spirographe classique avec plusieurs roues
        const angle = t * Math.PI * 2 * 4;
        const R = baseRadius * 0.5;
        const r1 = R / d.geometricFreq1;
        const r2 = r1 / d.geometricFreq2;
        const k1 = (R - r1) / r1;
        const k2 = (r1 - r2) / r2;
        x = centerX + (R - r1) * Math.cos(angle) + r1 * Math.cos(k1 * angle + d.modePhase) + r2 * Math.cos(k2 * angle * d.geometricFreq3);
        y = centerY + (R - r1) * Math.sin(angle) - r1 * Math.sin(k1 * angle + d.modePhase) + r2 * Math.sin(k2 * angle * d.geometricFreq3);
        break;
      }
      
      case GENERATION_MODES.FERMAT_SPIRAL: {
        // Spirale de Fermat (parabolic spiral)
        const angle = t * Math.PI * 8 + d.modePhase;
        const r = baseRadius * 0.8 * Math.sqrt(Math.abs(angle));
        const sign = Math.sin(t * Math.PI * d.geometricFreq1) > 0 ? 1 : -1;
        x = centerX + sign * r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      
      case GENERATION_MODES.GOLDEN_SPIRAL: {
        // Spirale dorée (approximation basée sur phi)
        const phi = 1.618033988749895;
        const angle = t * Math.PI * 6 + d.modePhase;
        const r = baseRadius * 0.02 * Math.pow(phi, angle / (Math.PI / 2));
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      
      case GENERATION_MODES.SUPERELLIPSE: {
        // Superellipse (Lamé curve) avec exposant variable
        const angle = t * Math.PI * 2 + d.modePhase;
        const n = 2 + d.geometricFreq1 * 0.5; // exposant entre 2 et ~4
        const a = baseRadius * 0.7;
        const b = baseRadius * 0.5;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);
        const signX = cosA >= 0 ? 1 : -1;
        const signY = sinA >= 0 ? 1 : -1;
        x = centerX + signX * a * Math.pow(Math.abs(cosA), 2/n);
        y = centerY + signY * b * Math.pow(Math.abs(sinA), 2/n);
        break;
      }
      
      case GENERATION_MODES.BUTTERFLY_CURVE: {
        // Courbe papillon de Temple Fay
        const angle = t * Math.PI * 24 + d.modePhase;
        const factor = Math.exp(Math.cos(angle)) - 2 * Math.cos(4 * angle) - Math.pow(Math.sin(angle / 12), 5);
        const r = baseRadius * 0.25 * Math.abs(factor);
        x = centerX + r * Math.sin(angle);
        y = centerY - r * Math.cos(angle);
        break;
      }
      
      case GENERATION_MODES.MAURER_ROSE: {
        // Rose de Maurer - lignes connectant points d'une rose
        const n = Math.floor(d.petalCount);
        const dDeg = d.maurerDegree;
        const k = t * 360;
        const theta = k * dDeg * Math.PI / 180 + d.modePhase;
        const r = baseRadius * 0.7 * Math.sin(n * theta);
        x = centerX + r * Math.cos(theta);
        y = centerY + r * Math.sin(theta);
        break;
      }
      
      case GENERATION_MODES.HARMONOGRAPH: {
        // Harmonographe - pendules couplés
        const time = t * Math.PI * 8;
        const f1 = d.geometricFreq1;
        const f2 = d.geometricFreq2;
        const f3 = d.geometricFreq3;
        const p1 = d.modePhase;
        const p2 = d.phaseShift;
        const decay = Math.pow(d.harmonicDamping, time);
        x = centerX + baseRadius * 0.5 * (Math.sin(time * f1 + p1) + Math.sin(time * f3 + p2) * 0.5) * decay;
        y = centerY + baseRadius * 0.5 * (Math.sin(time * f2 + p2) + Math.sin(time * f1 * 0.5) * 0.5) * decay;
        break;
      }
      
      case GENERATION_MODES.RHODONEA: {
        // Rose généralisée (rhodonea) avec ratio k = n/d
        const n = d.geometricFreq1;
        const denom = d.geometricFreq2;
        const k = n / denom;
        const angle = t * Math.PI * 2 * denom + d.modePhase;
        const r = baseRadius * 0.7 * Math.cos(k * angle);
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      
      case GENERATION_MODES.EPITROCHOID: {
        // Épitrochoïde - généralisation de l'épicycloïde
        const angle = t * Math.PI * 2 * 3;
        const R = baseRadius * 0.4;
        const r = R / d.geometricFreq1;
        const dVal = r * d.modulationDepth * 2;
        x = centerX + (R + r) * Math.cos(angle) - dVal * Math.cos((R + r) / r * angle + d.modePhase);
        y = centerY + (R + r) * Math.sin(angle) - dVal * Math.sin((R + r) / r * angle + d.modePhase);
        break;
      }
      
      case GENERATION_MODES.INVOLUTE: {
        // Involute de cercle
        const angle = t * Math.PI * 4 + d.modePhase;
        const a = baseRadius * 0.15;
        x = centerX + a * (Math.cos(angle) + angle * Math.sin(angle));
        y = centerY + a * (Math.sin(angle) - angle * Math.cos(angle));
        break;
      }
      
      case GENERATION_MODES.LOGARITHMIC_SPIRAL: {
        // Spirale logarithmique
        const angle = t * Math.PI * 6 + d.modePhase;
        const a = baseRadius * 0.05;
        const b = 0.15 + d.spiralTightness * 0.1;
        const r = a * Math.exp(b * angle);
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      
      case GENERATION_MODES.COTES_SPIRAL: {
        // Spirale de Cotes
        const angle = t * Math.PI * 4 + d.modePhase;
        const A = baseRadius * 0.5;
        const k = d.geometricFreq1 * 0.3;
        const r = A / Math.cosh(k * angle);
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      
      case GENERATION_MODES.TALBOT_CURVE: {
        // Courbe de Talbot
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.5;
        const f = d.eccentricity;
        const f2 = f * f;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);
        x = centerX + a * (1 + f2 * sinA * sinA) * cosA;
        y = centerY + a * (1 - f2 - f2 * cosA * cosA) * sinA;
        break;
      }
      
      case GENERATION_MODES.BICORN: {
        // Bicorne (chapeau de cocked hat)
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.6;
        const sinA = Math.sin(angle);
        const cosA = Math.cos(angle);
        x = centerX + a * sinA;
        y = centerY + a * cosA * cosA * (2 + cosA) / (3 + sinA * sinA);
        break;
      }
      
      case GENERATION_MODES.FOLIUM: {
        // Folium de Descartes
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.8;
        const tanA = Math.tan(angle);
        const denom = 1 + tanA * tanA * tanA;
        if (Math.abs(denom) > 0.01) {
          x = centerX + a * 3 * tanA / denom;
          y = centerY + a * 3 * tanA * tanA / denom;
        }
        break;
      }
      
      case GENERATION_MODES.LEMNISCATE: {
        // Lemniscate de Bernoulli
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.6;
        const cos2 = Math.cos(2 * angle);
        if (cos2 > 0) {
          const r = a * Math.sqrt(cos2);
          x = centerX + r * Math.cos(angle);
          y = centerY + r * Math.sin(angle);
        } else {
          const r = a * Math.sqrt(-cos2);
          x = centerX + r * Math.sin(angle);
          y = centerY + r * Math.cos(angle);
        }
        break;
      }
      
      case GENERATION_MODES.TREFOIL: {
        // Nœud de trèfle
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.4;
        x = centerX + a * (Math.sin(angle) + 2 * Math.sin(2 * angle));
        y = centerY + a * (Math.cos(angle) - 2 * Math.cos(2 * angle));
        break;
      }
      
      case GENERATION_MODES.QUADRIFOLIUM: {
        // Quadrifolium (rose à 4 pétales)
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.7;
        const r = a * Math.sin(2 * angle);
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
        break;
      }
      
      case GENERATION_MODES.RANUNCULOID: {
        // Ranunculoid - épicycloïde avec rapport 5
        const angle = t * Math.PI * 2 + d.modePhase;
        const a = baseRadius * 0.12;
        x = centerX + a * (6 * Math.cos(angle) - Math.cos(6 * angle));
        y = centerY + a * (6 * Math.sin(angle) - Math.sin(6 * angle));
        break;
      }
      
      case GENERATION_MODES.COCHLEOID: {
        // Cochléoïde
        const angle = t * Math.PI * 6 - Math.PI * 3 + d.modePhase;
        const a = baseRadius * 0.4;
        if (Math.abs(angle) > 0.01) {
          const r = a * Math.sin(angle) / angle;
          x = centerX + r * Math.cos(angle);
          y = centerY + r * Math.sin(angle);
        }
        break;
      }
      
      default: {
        // Fallback sur spirale de Fibonacci
        const angle = t * Math.PI * 2 * d.geometricFreq1 + d.modePhase;
        const r = baseRadius * Math.sqrt(t) * (1 + d.spiralTightness);
        x = centerX + r * Math.cos(angle);
        y = centerY + r * Math.sin(angle);
      }
    }
    
    // Perturbation organique via noise
    const noiseX = noise(x * 0.005, y * 0.005, d.organicPhase) * minDim * 0.05 * d.turbulenceScale;
    const noiseY = noise(x * 0.005 + 100, y * 0.005 + 100, d.organicPhase) * minDim * 0.05 * d.turbulenceScale;
    x += noiseX;
    y += noiseY;
    
    // Warp central
    const distFromCenter = Math.sqrt((x - centerX)**2 + (y - centerY)**2);
    const warpFactor = Math.exp(-distFromCenter / (minDim * 0.3)) * d.centerWarp * minDim * 0.1;
    const warpAngle = Math.atan2(y - centerY, x - centerX) + d.modePhase;
    x += Math.cos(warpAngle) * warpFactor;
    y += Math.sin(warpAngle) * warpFactor;
    
    return { x, y };
  };

  const resetParticle = useCallback((p, w, h, minDim) => {
    const d = dna.current;
    const particleIndex = Math.random();
    const pos = generateGeometricPosition(particleIndex, 1, w, h, minDim, d);
    p.x = pos.x;
    p.y = pos.y;
    
    const traitType = Math.random();
    p.isCalligraphy = traitType < 0.15;
    p.isDot = traitType > 0.15 && traitType < 0.75;
    p.isStroke = traitType >= 0.75;
    
    p.hueOffset = Math.random() * 40 - 20;
    p.waterLevel = Math.random();
    p.sizeVar = Math.random() * 1.5 + 0.5;
    
    p.life = 0;

    if (displayMode === 'white') {
      p.lifeSpeed = p.isCalligraphy ? (0.0008 + Math.random()*0.0008) : (0.0002 + Math.random()*0.0004); 
    } else {
      p.lifeSpeed = p.isCalligraphy ? (0.001 + Math.random()*0.001) : (0.0004 + Math.random()*0.0006);
    }
    
    const centerX = w / 2;
    const centerY = h / 2;
    const angle = Math.atan2(p.y - centerY, p.x - centerX);
    const tangentAngle = angle + Math.PI / 2;
    const speed = 0.08 + Math.random() * 0.15;
    
    p.vx = Math.cos(tangentAngle) * speed; 
    p.vy = Math.sin(tangentAngle) * speed;
    
    p.curveBias = (Math.random() - 0.5) * 0.08;
    p.distortion = Math.random() * Math.PI * 2;
    p.rotationSpeed = (Math.random() - 0.5) * 0.02;
    p.scaleOscillation = Math.random() * Math.PI * 2;
    p.shapeComplexity = 3 + Math.floor(Math.random() * 5);
  }, [displayMode]); 

  const initBrushes = useCallback((w, h) => {
    brushes.current = [];
    const count = displayMode === 'white' ? 120 : 150;
    const minDim = Math.min(w, h);
    for (let i = 0; i < count; i++) {
      const p = {};
      resetParticle(p, w, h, minDim);
      p.life = Math.random();
      brushes.current.push(p);
    }
  }, [displayMode, resetParticle]);

  /* — HYPERSPACE ENGINE (nouveau moteur simplifié) — */
  const resetHyperspaceParticle = useCallback((p, w, h, minDim) => {
    const d = dna.current;
    const t = Math.random();
    const baseRadius = minDim * 0.4;
    const angle = t * Math.PI * 2 * d.geometricFreq1 + d.modePhase;
    const r = baseRadius * Math.sqrt(t);
    p.x = w/2 + r * Math.cos(angle) + noise(t*10, d.organicPhase, 0) * minDim * 0.05;
    p.y = h/2 + r * Math.sin(angle) + noise(t*10+100, d.organicPhase, 0) * minDim * 0.05;
    p.hueOffset = Math.random() * 40 - 20;
    p.waterLevel = Math.random();
    p.sizeVar = Math.random() * 1.5 + 0.5;
    p.life = 0;
    p.lifeSpeed = 0.0003 + Math.random() * 0.0003;
    const a = Math.atan2(p.y - h/2, p.x - w/2) + Math.PI/2;
    p.vx = Math.cos(a) * 0.1;
    p.vy = Math.sin(a) * 0.1;
    p.distortion = Math.random() * Math.PI * 2;
  }, []);

  const initHyperspace = useCallback((w, h) => {
    hyperspaceParticles.current = [];
    const minDim = Math.min(w, h);
    for (let i = 0; i < 100; i++) {
      const p = {};
      resetHyperspaceParticle(p, w, h, minDim);
      p.life = Math.random();
      hyperspaceParticles.current.push(p);
    }
  }, [resetHyperspaceParticle]);

  const resetCanvas = useCallback(() => {
    if (!canvasRef.current) return;
    const { w, h } = canvasSize.current;
    const ctx = canvasRef.current.getContext('2d');
    const pal = getActivePaletteData();
    
    ctx.fillStyle = pal.bg;
    ctx.fillRect(0, 0, w, h);
    initBrushes(w, h);
    dna.current.washCycle = 0;
    bubblesParticles.current = [];
    bubblesState.current.spawnAccumulator = 0;
    hyperspaceParticles.current = [];
    glowSim.current = null;
    rosaceVirtualTime.current = 0;
    globalSpeed.current = 1.0;
    targetGlobalSpeed.current = 1.0;
  }, [initBrushes, getActivePaletteData]);

  const toggleDisplayMode = () => {
    const newMode = displayMode === 'white' ? 'black' : 'white';
    setDisplayMode(newMode);
    // Au changement de monde, on repasse en mode palette fixe sur la première palette pour une transition propre
    setPaletteMode('fixed');
    dna.current.paletteIndex = 0;
  };

  // Sync body/html background with displayMode to prevent bands on mobile
  useEffect(() => {
    const bg = displayMode === 'white' ? '#fdfdfd' : '#000000';
    document.body.style.background = bg;
    document.documentElement.style.background = bg;
  }, [displayMode]);

  /* — LOGIQUE DE CHANGEMENT DE PALETTE — */
  const selectPalette = (index) => {
    setPaletteMode('fixed');
    dna.current.paletteIndex = index;
    // Mise à jour immédiate du nom
    const palettes = getPalettes();
    setCurrentPaletteName(palettes[index].name);
    setShowPaletteMenu(false);
  };

  const selectAutoMode = () => {
    setPaletteMode('auto');
    autoSwitchTime.current = Date.now() + 30000 + Math.random() * 60000;
    setCurrentPaletteName("Mode Auto");
    setShowPaletteMenu(false);
  };

  const selectFusionMode = () => {
    setPaletteMode('fusion');
    setCurrentPaletteName("Fusion Totale");
    setShowPaletteMenu(false);
  };

  /* — DESSIN (Primitives) — */
  const drawPolygon = (ctx, x, y, size, sides, rotation, distortion) => {
    ctx.beginPath();
    for (let i = 0; i <= sides; i++) {
      const angle = (i / sides) * Math.PI * 2 + rotation;
      const r = size * (1 + Math.sin(angle * 2 + distortion) * 0.2);
      const px = x + Math.cos(angle) * r;
      const py = y + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  };

  const drawOrganicBlob = (ctx, x, y, size, distortion, complexity) => {
    ctx.beginPath();
    const steps = Math.floor(complexity);
    for (let i = 0; i <= steps; i++) {
      const angle = (i / steps) * Math.PI * 2;
      const noiseVal = noise(x * 0.01 + Math.cos(angle), y * 0.01 + Math.sin(angle), distortion);
      const r = size * (1 + noiseVal * 0.4);
      const px = x + Math.cos(angle) * r;
      const py = y + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(px, py);
      else {
        const prevAngle = ((i-1) / steps) * Math.PI * 2;
        const prevR = size * (1 + noise(x * 0.01 + Math.cos(prevAngle), y * 0.01 + Math.sin(prevAngle), distortion) * 0.4);
        const prevX = x + Math.cos(prevAngle) * prevR;
        const prevY = y + Math.sin(prevAngle) * prevR;
        const cx = (prevX + px) / 2;
        const cy = (prevY + py) / 2;
        ctx.quadraticCurveTo(prevX, prevY, cx, cy);
      }
    }
    ctx.closePath();
    ctx.fill();
  };

  const drawCalligraphy = (ctx, x, y, vx, vy, size, life, rotation) => {
    const angle = Math.atan2(vy, vx) + rotation;
    const length = size * 4 * (1 + Math.abs(vx+vy)*2);
    const thickness = size * Math.sin(life * Math.PI) * 0.8;
    ctx.beginPath();
    ctx.ellipse(x, y, length, thickness, angle, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawStroke = (ctx, x, y, vx, vy, size, life) => {
    const angle = Math.atan2(vy, vx);
    const length = size * 3;
    const startX = x - Math.cos(angle) * length * life;
    const startY = y - Math.sin(angle) * length * life;
    const endX = x + Math.cos(angle) * length * (1 - life);
    const endY = y + Math.sin(angle) * length * (1 - life);
    
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.lineWidth = size * (1 - Math.abs(life - 0.5) * 2);
    ctx.stroke();
  };

  const evolve = useCallback(() => {
    const d = dna.current;
    const spd = globalSpeed.current;

    d.brushSizeBase = 12 + Math.sin(d.time * 0.1) * 6;
    d.wetness = 0.012 + Math.cos(d.time * 0.05) * 0.005;

    d.organicPhase += 0.00001 * spd;
    d.structureComplexity = (Math.sin(d.organicPhase * 0.5) + 1) * 0.5;

    d.modePhase += 0.00005 * spd;
    d.phaseShift += 0.00003 * spd;
    
    // Évolution des paramètres géométriques
    d.geometricFreq1 = 3 + Math.sin(d.organicPhase * 0.3) * 2;
    d.geometricFreq2 = 5 + Math.cos(d.organicPhase * 0.4) * 3;
    d.geometricFreq3 = 7 + Math.sin(d.organicPhase * 0.25) * 4;
    d.geometricAmp = 0.3 + Math.sin(d.organicPhase * 0.6) * 0.2;
    d.geometricAmp2 = 0.2 + Math.cos(d.organicPhase * 0.7) * 0.15;
    
    d.waveComplexity = 2 + Math.sin(d.organicPhase * 0.2) * 1.5;
    d.turbulenceScale = 0.8 + Math.cos(d.organicPhase * 0.15) * 0.4;
    d.centerWarp = Math.sin(d.organicPhase * 0.25) * 0.5;
    
    // Évolution des nouveaux paramètres
    d.modulationDepth = 0.5 + Math.sin(d.organicPhase * 0.35) * 0.3;
    d.eccentricity = 0.5 + Math.cos(d.organicPhase * 0.45) * 0.3;
    d.petalCount = 3 + Math.floor((Math.sin(d.organicPhase * 0.1) + 1) * 4); // 3 à 11 pétales
    d.harmonicDamping = 0.99 + Math.sin(d.organicPhase * 0.2) * 0.008;
    
    // Changement de symétrie
    if (Math.random() < 0.00008) {
      const syms = [5, 6, 7, 8, 9, 10, 12, 16]; 
      d.symmetry = syms[Math.floor(Math.random() * syms.length)];
    }
    
    // Changement de mode géométrique - maintenant avec tous les nouveaux modes
    if (Math.random() < 0.00004) {
      const modes = Object.values(GENERATION_MODES);
      d.generationMode = modes[Math.floor(Math.random() * modes.length)];
      d.radialLayers = 2 + Math.floor(Math.random() * 4);
      d.spiralTightness = Math.random() * 0.3;
      // Réinitialiser certains paramètres pour le nouveau mode
      d.maurerDegree = [29, 37, 47, 53, 59, 67, 71, 79, 83, 89, 97][Math.floor(Math.random() * 11)]; // Nombres premiers
    }
    
    d.washCycle += 0.001;
  }, []);

  const draw = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { w, h, minDim } = canvasSize.current;
    const d = dna.current;
    
    // GESTION MODE AUTO
    if (paletteMode === 'auto' && Date.now() > autoSwitchTime.current) {
        const palettes = getPalettes();
        // Choix aléatoire différent du courant
        let nextIndex = Math.floor(Math.random() * palettes.length);
        while(palettes.length > 1 && nextIndex === d.paletteIndex) {
            nextIndex = Math.floor(Math.random() * palettes.length);
        }
        d.paletteIndex = nextIndex;
        // Intervalle entre 30s (30000) et 90s (30000 + 60000)
        autoSwitchTime.current = Date.now() + 30000 + Math.random() * 60000;
        // Mise à jour de l'affichage (via state, attention au re-render, ici acceptable car peu fréquent)
        setCurrentPaletteName(palettes[nextIndex].name);
    }

    const pal = getActivePaletteData();

    // Lerp global speed
    globalSpeed.current += (targetGlobalSpeed.current - globalSpeed.current) * 0.08;
    d.time += 0.005 * d.speed * globalSpeed.current;
    
    ctx.globalCompositeOperation = 'source-over';
    const fadeOpacity = 0.08 + (Math.sin(d.washCycle * 0.5) * 0.01);
    ctx.fillStyle = pal.bg; 
    ctx.globalAlpha = fadeOpacity;
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;
    
    if (displayMode === 'white') {
      ctx.globalCompositeOperation = 'multiply'; 
    } else {
      ctx.globalCompositeOperation = 'screen';
    }
    
    evolve();
    
    brushes.current.forEach(p => {
      const noiseVal = noise(p.x * d.zoom, p.y * d.zoom, d.time);
      
      p.life += p.lifeSpeed;
      if (p.life >= 1) resetParticle(p, w, h, minDim);
      const lifeOpacity = Math.sin(p.life * Math.PI);
      
      const angleFlow = noiseVal * Math.PI * 4;
      const angleCentric = Math.atan2(p.y - h/2, p.x - w/2) + Math.PI/2;
      
      const mixRatio = p.isCalligraphy ? 0.6 : 0.85; 
      const finalAngle = angleFlow * (1-mixRatio) + angleCentric * mixRatio;
      
      const pulse = noise(d.time, 0, 0); 
      const radiusForce = pulse * 0.0005 * (p.isCalligraphy ? 2 : 1);
      
      const moveSpeed = 0.00015; 
      
      p.vx += Math.cos(finalAngle) * moveSpeed + (Math.cos(angleCentric - Math.PI/2) * radiusForce);
      p.vy += Math.sin(finalAngle) * moveSpeed + (Math.sin(angleCentric - Math.PI/2) * radiusForce);
      
      if (p.isCalligraphy) {
        const vAngle = Math.atan2(p.vy, p.vx);
        p.vx = Math.cos(vAngle + p.curveBias) * Math.sqrt(p.vx*p.vx + p.vy*p.vy);
        p.vy = Math.sin(vAngle + p.curveBias) * Math.sqrt(p.vx*p.vx + p.vy*p.vy);
      }
      
      p.vx *= 0.94; 
      p.vy *= 0.94;
      p.x += p.vx * 2; 
      p.y += p.vy * 2;
      
      p.distortion += 0.008;
      p.scaleOscillation += 0.01;
      
      const colIndex = Math.floor(Math.abs(noiseVal * 10)) % pal.colors.length;
      const baseCol = pal.colors[colIndex];
      const hue = (baseCol[0] + p.hueOffset) % 360;
      
      const cx = w / 2;
      const cy = h / 2;
      
      for (let s = 0; s < d.symmetry; s++) {
        const rot = (Math.PI * 2 * s) / d.symmetry;
        const rx = (p.x - cx) * Math.cos(rot) - (p.y - cy) * Math.sin(rot) + cx;
        const ry = (p.x - cx) * Math.sin(rot) + (p.y - cy) * Math.cos(rot) + cy;
        
        const distFromCenterPoint = Math.sqrt((rx - cx)**2 + (ry - cy)**2);
        const safeZone = minDim * 0.15; 
        let centerFactor = (distFromCenterPoint / safeZone);
        centerFactor = Math.max(0, Math.min(1, centerFactor));
        const alphaMask = Math.pow(centerFactor, 3);
        
        const speed = Math.sqrt(p.vx*p.vx + p.vy*p.vy);
        let brushSize = d.brushSizeBase * p.sizeVar * (1 - speed) * (Math.abs(noiseVal)+0.5);
        brushSize *= (1 + Math.sin(p.scaleOscillation) * 0.2);
        
        if (displayMode === 'white') {
           let alpha = d.wetness * p.waterLevel * 2.0 * alphaMask * lifeOpacity; 
           if (alpha > 0.001) {
             ctx.fillStyle = `hsla(${hue}, ${baseCol[1]}%, ${baseCol[2]}%, ${alpha})`;
             ctx.strokeStyle = `hsla(${hue}, ${baseCol[1]}%, ${baseCol[2]}%, ${alpha})`;
             drawShapes(ctx, p, rx, ry, brushSize, rot);
           }
        } else {
           let alpha = d.wetness * p.waterLevel * 3.0 * alphaMask * lifeOpacity; 
           if (alpha > 0.001) {
             ctx.fillStyle = `hsla(${hue}, ${baseCol[1]}%, ${baseCol[2] * 1.2}%, ${alpha})`;
             ctx.strokeStyle = `hsla(${hue}, ${baseCol[1]}%, ${baseCol[2] * 1.2}%, ${alpha})`;
             drawShapes(ctx, p, rx, ry, brushSize, rot);
           }
        }
      }
    });
    
    if (isPlaying) {
      frameId.current = requestAnimationFrame(draw);
    }
  }, [isPlaying, evolve, displayMode, resetParticle, paletteMode, getPalettes, getActivePaletteData]);

  const drawShapes = (ctx, p, rx, ry, brushSize, rot) => {
    if (p.isCalligraphy) {
      const rvx = p.vx * Math.cos(rot) - p.vy * Math.sin(rot);
      const rvy = p.vx * Math.sin(rot) + p.vy * Math.cos(rot);
      drawCalligraphy(ctx, rx, ry, rvx, rvy, brushSize, p.life, p.distortion);
    } else if (p.isDot) {
      if (Math.random() > 0.5) {
        drawOrganicBlob(ctx, rx, ry, brushSize, p.distortion, p.shapeComplexity);
      } else {
        drawPolygon(ctx, rx, ry, brushSize, p.shapeComplexity, p.distortion, p.life * Math.PI * 2);
      }
    } else if (p.isStroke) {
      const rvx = p.vx * Math.cos(rot) - p.vy * Math.sin(rot);
      const rvy = p.vx * Math.sin(rot) + p.vy * Math.cos(rot);
      drawStroke(ctx, rx, ry, rvx, rvy, brushSize, p.life);
    }
  };

  /* — BUBBLES ENGINE — */
  const drawBubbles = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { w, h, minDim } = canvasSize.current;
    const bs = bubblesState.current;
    const d = dna.current;

    // Auto palette switching (shared logic)
    if (paletteMode === 'auto' && Date.now() > autoSwitchTime.current) {
      const palettes = getPalettes();
      let nextIndex = Math.floor(Math.random() * palettes.length);
      while (palettes.length > 1 && nextIndex === d.paletteIndex) {
        nextIndex = Math.floor(Math.random() * palettes.length);
      }
      d.paletteIndex = nextIndex;
      autoSwitchTime.current = Date.now() + 30000 + Math.random() * 60000;
      setCurrentPaletteName(palettes[nextIndex].name);
    }

    const pal = getActivePaletteData();

    // Lerp global speed
    globalSpeed.current += (targetGlobalSpeed.current - globalSpeed.current) * 0.08;
    const spd = globalSpeed.current;

    // Clear background completely each frame
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.fillStyle = pal.bg;
    ctx.fillRect(0, 0, w, h);

    const maxRadius = minDim * 0.45;

    // Spawn particles (vitesse modulée par globalSpeed)
    // Bubbles tourne à 40% de la vitesse globale pour un rendu zen
    const bubbleSpd = spd * 0.175;
    bs.spawnAccumulator += bubbleSpd * 0.6;
    while (bs.spawnAccumulator >= 1) {
      bubblesParticles.current.push({
        angle: Math.random() * Math.PI * 2,
        radialSpeed: 0.8 + Math.random() * 1.0,
        distance: maxRadius * (0.05 + Math.random() * 0.12),
        maxLife: 255,
        lifespan: 255,
        colorIndex: Math.floor(Math.random() * pal.colors.length),
        size: 20 + Math.random() * 40,
        posX: 0,
        posY: 0,
      });
      bs.spawnAccumulator -= 1;
    }

    // Set composite operation based on display mode
    if (displayMode === 'white') {
      ctx.globalCompositeOperation = 'multiply';
    } else {
      ctx.globalCompositeOperation = 'screen';
    }

    // Update and draw particles
    for (let i = bubblesParticles.current.length - 1; i >= 0; i--) {
      const p = bubblesParticles.current[i];
      const step = p.radialSpeed * bubbleSpd;
      p.distance += step;
      p.posX = Math.cos(p.angle) * p.distance;
      p.posY = Math.sin(p.angle) * p.distance;
      p.lifespan = p.maxLife * (1 - p.distance / maxRadius);

      if (p.lifespan > 0) {
        // Fade-in radial quadratique : très transparent au centre, monte progressivement
        const rawDist = Math.min(1, p.distance / (maxRadius * 0.4));
        const distFactor = rawDist * rawDist; // courbe quadratique = montée douce
        const alpha = (p.lifespan / p.maxLife) * 0.22 * distFactor;
        const col = pal.colors[p.colorIndex % pal.colors.length];
        const lum = displayMode === 'white' ? col[2] : Math.min(col[2] * 1.2, 100);

        ctx.fillStyle = `hsla(${col[0]}, ${col[1]}%, ${lum}%, ${alpha})`;

        ctx.save();
        ctx.translate(w / 2, h / 2);

        for (let s = 0; s < 8; s++) {
          ctx.save();
          ctx.rotate(s * Math.PI / 4);

          ctx.beginPath();
          ctx.arc(p.posX, p.posY, p.size / 2, 0, Math.PI * 2);
          ctx.fill();

          ctx.save();
          ctx.scale(1, -1);
          ctx.beginPath();
          ctx.arc(p.posX, p.posY, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          ctx.restore();
        }

        ctx.restore();
      }

      if (p.distance >= maxRadius) {
        bubblesParticles.current.splice(i, 1);
      }
    }

    // Masque central : disque dégradé pour cacher le scintillement
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    const maskFade = maxRadius * 0.20;
    const bgRgb = hexToRgb(pal.bg);
    const bgR = Math.round(bgRgb[0] * 255), bgG = Math.round(bgRgb[1] * 255), bgB = Math.round(bgRgb[2] * 255);
    const grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, maskFade);
    grad.addColorStop(0, `rgba(${bgR},${bgG},${bgB},1)`);
    grad.addColorStop(0.4, `rgba(${bgR},${bgG},${bgB},0.9)`);
    grad.addColorStop(0.65, `rgba(${bgR},${bgG},${bgB},0.35)`);
    grad.addColorStop(0.85, `rgba(${bgR},${bgG},${bgB},0.08)`);
    grad.addColorStop(1, `rgba(${bgR},${bgG},${bgB},0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, maskFade, 0, Math.PI * 2);
    ctx.fill();

    if (isPlaying) {
      frameId.current = requestAnimationFrame(drawBubbles);
    }
  }, [isPlaying, displayMode, paletteMode, getPalettes, getActivePaletteData]);

  /* — HYPERSPACE DRAW (moteur simplifié — cercles + noise flow) — */
  const drawHyperspace = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { w, h, minDim } = canvasSize.current;
    const d = dna.current;

    // Auto palette switching
    if (paletteMode === 'auto' && Date.now() > autoSwitchTime.current) {
      const palettes = getPalettes();
      let nextIndex = Math.floor(Math.random() * palettes.length);
      while (palettes.length > 1 && nextIndex === d.paletteIndex) {
        nextIndex = Math.floor(Math.random() * palettes.length);
      }
      d.paletteIndex = nextIndex;
      autoSwitchTime.current = Date.now() + 30000 + Math.random() * 60000;
      setCurrentPaletteName(palettes[nextIndex].name);
    }

    const pal = getActivePaletteData();

    // Lerp global speed
    globalSpeed.current += (targetGlobalSpeed.current - globalSpeed.current) * 0.08;
    const spd = globalSpeed.current;

    // Simplified inline evolution (speed-modulated)
    d.organicPhase += 0.00001 * spd;
    d.modePhase += 0.00005 * spd;
    d.time += 0.005 * spd;
    d.brushSizeBase = 12 + Math.sin(d.time * 0.1) * 6;
    d.wetness = 0.012 + Math.cos(d.time * 0.05) * 0.005;
    d.geometricFreq1 = 3 + Math.sin(d.organicPhase * 0.3) * 2;
    if (Math.random() < 0.00008) {
      d.symmetry = [5, 6, 7, 8, 9, 10, 12, 16][Math.floor(Math.random() * 8)];
    }

    // Fade background
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = pal.bg;
    ctx.globalAlpha = 0.08;
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;

    ctx.globalCompositeOperation = displayMode === 'white' ? 'multiply' : 'screen';

    hyperspaceParticles.current.forEach(p => {
      const nv = noise(p.x * d.zoom, p.y * d.zoom, d.time);
      p.life += p.lifeSpeed;
      if (p.life >= 1) resetHyperspaceParticle(p, w, h, minDim);
      const lifeOp = Math.sin(p.life * Math.PI);
      const angleFlow = nv * Math.PI * 4;
      const angleCentric = Math.atan2(p.y - h / 2, p.x - w / 2) + Math.PI / 2;
      const finalAngle = angleFlow * 0.15 + angleCentric * 0.85;
      p.vx += Math.cos(finalAngle) * 0.00015;
      p.vy += Math.sin(finalAngle) * 0.00015;
      p.vx *= 0.94;
      p.vy *= 0.94;
      p.x += p.vx * 2;
      p.y += p.vy * 2;
      p.distortion += 0.008;

      const col = pal.colors[Math.floor(Math.abs(nv * 10)) % pal.colors.length];
      const hue = (col[0] + p.hueOffset) % 360;

      for (let s = 0; s < d.symmetry; s++) {
        const rot = s * Math.PI * 2 / d.symmetry;
        const rx = (p.x - w / 2) * Math.cos(rot) - (p.y - h / 2) * Math.sin(rot) + w / 2;
        const ry = (p.x - w / 2) * Math.sin(rot) + (p.y - h / 2) * Math.cos(rot) + h / 2;
        const dist = Math.sqrt((rx - w / 2) ** 2 + (ry - h / 2) ** 2);
        const alphaMask = Math.pow(Math.min(1, dist / (minDim * 0.15)), 3);
        const brushSize = d.brushSizeBase * p.sizeVar * (Math.abs(nv) + 0.5);
        const alpha = d.wetness * p.waterLevel * 2.5 * alphaMask * lifeOp;
        if (alpha > 0.001) {
          const lum = displayMode === 'white' ? col[2] : col[2] * 1.2;
          ctx.fillStyle = `hsla(${hue},${col[1]}%,${lum}%,${alpha})`;
          ctx.beginPath();
          ctx.arc(rx, ry, brushSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });

    if (isPlaying) {
      frameId.current = requestAnimationFrame(drawHyperspace);
    }
  }, [isPlaying, displayMode, paletteMode, getPalettes, getActivePaletteData, resetHyperspaceParticle]);

  /* — GLOW ENGINE (encre fluide vivante, façon suminagashi) — */
  const initGlow = useCallback(() => {
    if (glowSim.current) return; // on préserve la toile en cours
    const pal = getActivePaletteData();
    const bg = hexToRgb(pal.bg);
    const n = AQ_SIM * AQ_SIM;
    const field = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      field[i * 3] = bg[0] * 255;
      field[i * 3 + 1] = bg[1] * 255;
      field[i * 3 + 2] = bg[2] * 255;
    }
    const simCanvas = document.createElement('canvas');
    simCanvas.width = AQ_SIM;
    simCanvas.height = AQ_SIM;
    const simCtx = simCanvas.getContext('2d');
    const img = simCtx.createImageData(AQ_SIM, AQ_SIM);
    for (let i = 0; i < n; i++) img.data[i * 4 + 3] = 255;

    // Sources d'encre errantes — chacune porte une couleur de la palette
    const sources = [];
    for (let i = 0; i < 6; i++) {
      sources.push({
        seed: Math.random() * 1000,
        colIndex: i % pal.colors.length,
        r: bg[0] * 255, g: bg[1] * 255, b: bg[2] * 255,
      });
    }

    const sim = {
      field,
      tmp: new Float32Array(n * 3),
      vx: new Float32Array(AQ_VEL * AQ_VEL),
      vy: new Float32Array(AQ_VEL * AQ_VEL),
      canvas: simCanvas,
      ctx: simCtx,
      img,
      sources,
      velTime: Math.random() * 100,
      srcTime: Math.random() * 100,
      pointer: null,
      // Variation de symétrie : minuterie aléatoire + fondu enchaîné
      symCurrent: dna.current.symmetry,
      symNext: null,
      symBlend: 0,
      symCountdown: (45 + Math.random() * 75) * 60, // 45 à 120 s (en frames)
    };

    // Quelques flaques initiales pour ne pas démarrer sur une toile vide
    for (let i = 0; i < 8; i++) {
      const col = pal.colors[i % pal.colors.length];
      const lum = displayMode === 'white' ? col[2] : Math.min(col[2] * 1.1, 62);
      const rgb = hslToRgb(col[0], col[1], lum);
      const px = AQ_SIM * (0.2 + Math.random() * 0.6);
      const py = AQ_SIM * (0.2 + Math.random() * 0.6);
      const R = AQ_SIM * (0.06 + Math.random() * 0.08);
      const R2 = R * R;
      const x0 = Math.max(0, (px - R) | 0), x1 = Math.min(AQ_SIM - 1, (px + R) | 0);
      const y0 = Math.max(0, (py - R) | 0), y1 = Math.min(AQ_SIM - 1, (py + R) | 0);
      for (let yy = y0; yy <= y1; yy++) {
        for (let xx = x0; xx <= x1; xx++) {
          const dx = xx - px, dy = yy - py;
          const d2 = dx * dx + dy * dy;
          if (d2 > R2) continue;
          const g = Math.exp(-d2 / (R2 * 0.35));
          const o = (yy * AQ_SIM + xx) * 3;
          field[o] += (rgb[0] * 255 - field[o]) * g;
          field[o + 1] += (rgb[1] * 255 - field[o + 1]) * g;
          field[o + 2] += (rgb[2] * 255 - field[o + 2]) * g;
        }
      }
    }

    glowSim.current = sim;
  }, [getActivePaletteData, displayMode]);

  const drawGlow = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { w, h, minDim } = canvasSize.current;
    const d = dna.current;

    // Auto palette switching (logique partagée)
    if (paletteMode === 'auto' && Date.now() > autoSwitchTime.current) {
      const palettes = getPalettes();
      let nextIndex = Math.floor(Math.random() * palettes.length);
      while (palettes.length > 1 && nextIndex === d.paletteIndex) {
        nextIndex = Math.floor(Math.random() * palettes.length);
      }
      d.paletteIndex = nextIndex;
      autoSwitchTime.current = Date.now() + 30000 + Math.random() * 60000;
      setCurrentPaletteName(palettes[nextIndex].name);
    }

    const pal = getActivePaletteData();
    if (!glowSim.current) initGlow();
    const sim = glowSim.current;

    // Lerp global speed
    // Glow tourne à 18% de la vitesse globale pour un défaut vraiment doux
    globalSpeed.current += (targetGlobalSpeed.current - globalSpeed.current) * 0.08;
    const spd = globalSpeed.current * 0.18;

    // Géométrie du kaléidoscope
    const cx = w / 2, cy = h / 2;
    const Rout = minDim * 0.485;
    const scale = Rout / (AQ_SIM / 2 * 0.95);

    // Variation du nombre de symétries : déclenchement à intervalle
    // aléatoire (45-120 s), puis fondu enchaîné de ~5 s entre les deux
    // géométries — aucune rupture visuelle
    if (sim.symNext === null) {
      sim.symCountdown -= 1;
      if (sim.symCountdown <= 0) {
        const choices = [6, 8, 10, 12].filter(s => s !== sim.symCurrent);
        sim.symNext = choices[Math.floor(Math.random() * choices.length)];
        sim.symBlend = 0;
      }
    } else {
      sim.symBlend += 1 / 300;
      if (sim.symBlend >= 1) {
        sim.symCurrent = sim.symNext;
        sim.symNext = null;
        d.symmetry = sim.symCurrent;
        sim.symCountdown = (45 + Math.random() * 75) * 60;
      }
    }
    // L'encre est repliée dans le plus étroit des deux quartiers :
    // elle reste visible dans les deux géométries pendant le fondu
    const foldSym = sim.symNext !== null ? Math.max(sim.symCurrent, sim.symNext) : sim.symCurrent;
    const wedgeAngle = Math.PI / foldSym;

    // 1) Champ de vitesse : curl noise = flux incompressible, volutes d'encre
    sim.velTime += 0.0022 * spd;
    const freq = 2.4 / AQ_VEL;
    const eps = 0.8 * freq;
    for (let j = 0; j < AQ_VEL; j++) {
      for (let i = 0; i < AQ_VEL; i++) {
        const nx = i * freq, ny = j * freq;
        const idx = j * AQ_VEL + i;
        sim.vx[idx] = (noise(nx, ny + eps, sim.velTime) - noise(nx, ny - eps, sim.velTime)) * 7;
        sim.vy[idx] = -(noise(nx + eps, ny, sim.velTime) - noise(nx - eps, ny, sim.velTime)) * 7;
      }
    }

    // 2) Advection semi-lagrangienne + très lent retour vers le fond
    //    (le fondu garantit que les couches ne saturent jamais)
    const bgRgb = hexToRgb(pal.bg);
    const bgR = bgRgb[0] * 255, bgG = bgRgb[1] * 255, bgB = bgRgb[2] * 255;
    const fade = Math.min(0.0013 * spd, 0.04);
    const { field, tmp } = sim;
    const velScale = (AQ_VEL - 1) / AQ_SIM;
    for (let y = 0; y < AQ_SIM; y++) {
      const gy = y * velScale;
      let gj = gy | 0; if (gj > AQ_VEL - 2) gj = AQ_VEL - 2;
      const fy = gy - gj;
      for (let x = 0; x < AQ_SIM; x++) {
        const gx = x * velScale;
        let gi = gx | 0; if (gi > AQ_VEL - 2) gi = AQ_VEL - 2;
        const fx = gx - gi;
        const i00 = gj * AQ_VEL + gi;
        const vxv = (sim.vx[i00] * (1 - fx) + sim.vx[i00 + 1] * fx) * (1 - fy)
                  + (sim.vx[i00 + AQ_VEL] * (1 - fx) + sim.vx[i00 + AQ_VEL + 1] * fx) * fy;
        const vyv = (sim.vy[i00] * (1 - fx) + sim.vy[i00 + 1] * fx) * (1 - fy)
                  + (sim.vy[i00 + AQ_VEL] * (1 - fx) + sim.vy[i00 + AQ_VEL + 1] * fx) * fy;
        let sx = x - vxv * spd;
        let sy = y - vyv * spd;
        if (sx < 0) sx = 0; else if (sx > AQ_SIM - 1.001) sx = AQ_SIM - 1.001;
        if (sy < 0) sy = 0; else if (sy > AQ_SIM - 1.001) sy = AQ_SIM - 1.001;
        const x0 = sx | 0, y0 = sy | 0;
        const tx = sx - x0, ty = sy - y0;
        const p00 = (y0 * AQ_SIM + x0) * 3;
        const p01 = p00 + AQ_SIM * 3;
        const o = (y * AQ_SIM + x) * 3;
        const r = (field[p00] * (1 - tx) + field[p00 + 3] * tx) * (1 - ty)
                + (field[p01] * (1 - tx) + field[p01 + 3] * tx) * ty;
        const g = (field[p00 + 1] * (1 - tx) + field[p00 + 4] * tx) * (1 - ty)
                + (field[p01 + 1] * (1 - tx) + field[p01 + 4] * tx) * ty;
        const b = (field[p00 + 2] * (1 - tx) + field[p00 + 5] * tx) * (1 - ty)
                + (field[p01 + 2] * (1 - tx) + field[p01 + 5] * tx) * ty;
        tmp[o] = r + (bgR - r) * fade;
        tmp[o + 1] = g + (bgG - g) * fade;
        tmp[o + 2] = b + (bgB - b) * fade;
      }
    }
    sim.field = tmp;
    sim.tmp = field;
    const fld = sim.field;

    // 3) Injection d'encre par les sources errantes
    sim.srcTime += 0.0008 * spd;
    const injectBlob = (px, py, R, sr, sg, sb, strength) => {
      const R2 = R * R;
      const x0 = Math.max(0, (px - R) | 0), x1 = Math.min(AQ_SIM - 1, (px + R) | 0);
      const y0 = Math.max(0, (py - R) | 0), y1 = Math.min(AQ_SIM - 1, (py + R) | 0);
      for (let yy = y0; yy <= y1; yy++) {
        for (let xx = x0; xx <= x1; xx++) {
          const dx = xx - px, dy = yy - py;
          const d2 = dx * dx + dy * dy;
          if (d2 > R2) continue;
          const gw = Math.exp(-d2 / (R2 * 0.35)) * strength;
          const o = (yy * AQ_SIM + xx) * 3;
          fld[o] += (sr - fld[o]) * gw;
          fld[o + 1] += (sg - fld[o + 1]) * gw;
          fld[o + 2] += (sb - fld[o + 2]) * gw;
        }
      }
    };

    sim.sources.forEach(s => {
      const t = sim.srcTime + s.seed;
      // L'angle est replié dans le quartier visible du kaléidoscope :
      // toute l'encre déposée participe au mandala
      const rawAng = noise(t * 0.9, s.seed, 0) * Math.PI * 3;
      const period = wedgeAngle * 2;
      let ang = rawAng % period;
      if (ang < 0) ang += period;
      if (ang > wedgeAngle) ang = period - ang;
      const rad = (0.08 + 0.38 * (noise(s.seed, t * 0.7, 5) * 0.5 + 0.5)) * AQ_SIM;
      const px = AQ_SIM / 2 + Math.cos(ang) * rad;
      const py = AQ_SIM / 2 + Math.sin(ang) * rad;
      // La couleur cible suit la palette courante (fixed/auto/fusion, jour/nuit)
      const col = pal.colors[s.colIndex % pal.colors.length];
      const lum = displayMode === 'white' ? col[2] : Math.min(col[2] * 1.1, 62);
      const target = hslToRgb(col[0], col[1], lum);
      s.r += (target[0] * 255 - s.r) * 0.012;
      s.g += (target[1] * 255 - s.g) * 0.012;
      s.b += (target[2] * 255 - s.b) * 0.012;
      if (Math.random() < 0.0004) s.colIndex = Math.floor(Math.random() * pal.colors.length);
      injectBlob(px, py, AQ_SIM * 0.095, s.r, s.g, s.b, 0.075 * Math.min(spd, 1.5));
    });

    // 4) Le doigt dépose de l'encre : on replie le point touché dans le quartier source
    if (sim.pointer) {
      const dx = sim.pointer.x - cx, dy = sim.pointer.y - cy;
      const pr = Math.sqrt(dx * dx + dy * dy);
      if (pr < Rout) {
        const period = wedgeAngle * 2;
        let a = Math.atan2(dy, dx) % period;
        if (a < 0) a += period;
        if (a > wedgeAngle) a = period - a;
        const simR = pr / scale;
        const px = AQ_SIM / 2 + Math.cos(a) * simR;
        const py = AQ_SIM / 2 + Math.sin(a) * simR;
        const col = pal.colors[Math.floor(Math.random() * pal.colors.length)];
        const lum = displayMode === 'white' ? col[2] : Math.min(col[2] * 1.1, 62);
        const rgb = hslToRgb(col[0], col[1], lum);
        injectBlob(px, py, AQ_SIM * 0.05, rgb[0] * 255, rgb[1] * 255, rgb[2] * 255, 0.12);
      }
      sim.pointer = null;
    }

    // 5) Rendu de la grille vers le canvas de simulation
    const data = sim.img.data;
    for (let i = 0, n = AQ_SIM * AQ_SIM; i < n; i++) {
      data[i * 4] = fld[i * 3];
      data[i * 4 + 1] = fld[i * 3 + 1];
      data[i * 4 + 2] = fld[i * 3 + 2];
    }
    sim.ctx.putImageData(sim.img, 0, 0);

    // 6) Kaléidoscope : 2×symétrie quartiers alternés en miroir (sans coutures)
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.fillStyle = pal.bg;
    ctx.fillRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = true;
    const drawKaleido = (sym) => {
      const wa = Math.PI / sym;
      const segs = sym * 2;
      for (let s2 = 0; s2 < segs; s2++) {
        ctx.save();
        ctx.translate(cx, cy);
        if (s2 % 2 === 0) {
          ctx.rotate(s2 * wa);
        } else {
          ctx.rotate((s2 + 1) * wa);
          ctx.scale(1, -1);
        }
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, Rout, -0.006, wa + 0.006);
        ctx.closePath();
        ctx.clip();
        ctx.scale(scale, scale);
        ctx.drawImage(sim.canvas, -AQ_SIM / 2, -AQ_SIM / 2);
        ctx.restore();
      }
    };
    drawKaleido(sim.symCurrent);
    if (sim.symNext !== null) {
      const t = sim.symBlend;
      ctx.globalAlpha = t * t * (3 - 2 * t); // smoothstep : fondu sans à-coups
      drawKaleido(sim.symNext);
      ctx.globalAlpha = 1;
    }

    // 7) Bord adouci : fondu circulaire vers le fond
    const bgRi = Math.round(bgR), bgGi = Math.round(bgG), bgBi = Math.round(bgB);
    const rim = ctx.createRadialGradient(cx, cy, Rout * 0.82, cx, cy, Rout);
    rim.addColorStop(0, `rgba(${bgRi},${bgGi},${bgBi},0)`);
    rim.addColorStop(0.7, `rgba(${bgRi},${bgGi},${bgBi},0.45)`);
    rim.addColorStop(1, `rgba(${bgRi},${bgGi},${bgBi},1)`);
    ctx.fillStyle = rim;
    ctx.beginPath();
    ctx.arc(cx, cy, Rout + 2, 0, Math.PI * 2);
    ctx.fill();

    if (isPlaying) {
      frameId.current = requestAnimationFrame(drawGlow);
    }
  }, [isPlaying, displayMode, paletteMode, getPalettes, getActivePaletteData, initGlow]);

  /* — ROSACE ENGINE (WebGL shader) — */
  const initRosace = useCallback(() => {
    const canvas = glCanvasRef.current;
    if (!canvas || glProgramRef.current) return; // already init
    const gl = canvas.getContext('webgl', { antialias: true, alpha: false });
    if (!gl) { console.error('WebGL not supported'); return; }
    glRef.current = gl;

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.error(gl.getShaderInfoLog(s)); return null; }
      return s;
    };
    const vs = compile(gl.VERTEX_SHADER, ROSACE_VS);
    const fs = compile(gl.FRAGMENT_SHADER, ROSACE_FS);
    const prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { console.error(gl.getProgramInfoLog(prog)); return; }
    glProgramRef.current = prog;

    glUniformsRef.current = {
      resolution: gl.getUniformLocation(prog, 'u_resolution'),
      time: gl.getUniformLocation(prog, 'u_time'),
      color0: gl.getUniformLocation(prog, 'u_color0'),
      color1: gl.getUniformLocation(prog, 'u_color1'),
      color2: gl.getUniformLocation(prog, 'u_color2'),
      color3: gl.getUniformLocation(prog, 'u_color3'),
      lightMode: gl.getUniformLocation(prog, 'u_lightMode'),
      bgColor: gl.getUniformLocation(prog, 'u_bgColor'),
      posAttr: gl.getAttribLocation(prog, 'aVertexPosition'),
    };

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([1,1, -1,1, 1,-1, -1,-1]), gl.STATIC_DRAW);
    glBufferRef.current = buf;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    gl.viewport(0, 0, canvas.width, canvas.height);
    rosaceVirtualTime.current = 0;
    lastFrameTime.current = performance.now();
  }, []);

  const drawRosace = useCallback(() => {
    const gl = glRef.current;
    const canvas = glCanvasRef.current;
    if (!gl || !canvas) return;

    // Auto palette switching
    if (paletteMode === 'auto' && Date.now() > autoSwitchTime.current) {
      const palettes = getPalettes();
      let nextIndex = Math.floor(Math.random() * palettes.length);
      while (palettes.length > 1 && nextIndex === dna.current.paletteIndex) {
        nextIndex = Math.floor(Math.random() * palettes.length);
      }
      dna.current.paletteIndex = nextIndex;
      autoSwitchTime.current = Date.now() + 30000 + Math.random() * 60000;
      setCurrentPaletteName(palettes[nextIndex].name);
    }

    const pal = getActivePaletteData();
    const u = glUniformsRef.current;
    const prog = glProgramRef.current;

    // Resize check (mobile-safe)
    const vv = window.visualViewport;
    const targetW = vv ? vv.width : window.innerWidth;
    const targetH = vv ? vv.height : window.innerHeight;
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
      canvas.style.width = targetW + 'px';
      canvas.style.height = targetH + 'px';
      gl.viewport(0, 0, targetW, targetH);
    }

    gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, glBufferRef.current);
    gl.vertexAttribPointer(u.posAttr, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(u.posAttr);

    // Lerp global speed + accumulate virtual time
    globalSpeed.current += (targetGlobalSpeed.current - globalSpeed.current) * 0.08;
    const now = performance.now();
    const dt = (now - lastFrameTime.current) * 0.001; // delta en secondes
    lastFrameTime.current = now;
    rosaceVirtualTime.current += dt * globalSpeed.current;

    gl.uniform2f(u.resolution, canvas.width, canvas.height);
    gl.uniform1f(u.time, rosaceVirtualTime.current);

    // Pass palette colors (HSL → RGB)
    const colors = pal.colors;
    const c0 = hslToRgb(...colors[0 % colors.length]);
    const c1 = hslToRgb(...colors[1 % colors.length]);
    const c2 = hslToRgb(...colors[2 % colors.length]);
    const c3 = hslToRgb(...colors[3 % colors.length]);
    gl.uniform3f(u.color0, c0[0], c0[1], c0[2]);
    gl.uniform3f(u.color1, c1[0], c1[1], c1[2]);
    gl.uniform3f(u.color2, c2[0], c2[1], c2[2]);
    gl.uniform3f(u.color3, c3[0], c3[1], c3[2]);

    gl.uniform1f(u.lightMode, displayMode === 'white' ? 1.0 : 0.0);
    const bg = hexToRgb(pal.bg);
    gl.uniform3f(u.bgColor, bg[0], bg[1], bg[2]);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    if (isPlaying) {
      frameId.current = requestAnimationFrame(drawRosace);
    }
  }, [isPlaying, displayMode, paletteMode, getPalettes, getActivePaletteData]);

  useEffect(() => {
    const pal = getActivePaletteData();
    setCurrentPaletteName(pal.name);
    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }
    handleResize();
    if (mandalaMode === 'morphogenesis') {
      frameId.current = requestAnimationFrame(draw);
    } else if (mandalaMode === 'bubbles') {
      bubblesParticles.current = [];
      bubblesState.current.spawnAccumulator = 0;
      bubblesState.current.currentSpeed = 0.5;
      bubblesState.current.targetSpeed = 0.5;
      frameId.current = requestAnimationFrame(drawBubbles);
    } else if (mandalaMode === 'hyperspace') {
      initHyperspace(canvasSize.current.w, canvasSize.current.h);
      frameId.current = requestAnimationFrame(drawHyperspace);
    } else if (mandalaMode === 'glow') {
      initGlow();
      frameId.current = requestAnimationFrame(drawGlow);
    } else if (mandalaMode === 'rosace') {
      initRosace();
      frameId.current = requestAnimationFrame(drawRosace);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
      cancelAnimationFrame(frameId.current);
    };
  }, [handleResize, draw, drawBubbles, drawHyperspace, drawGlow, drawRosace, initHyperspace, initGlow, initRosace, getActivePaletteData, mandalaMode]);

  // Contrôle de vitesse global : molette / trackpad
  const handleWheel = (e) => {
    e.preventDefault();
    // deltaY négatif = scroll up = plus vite, positif = scroll down = plus lent
    const delta = -e.deltaY * 0.001;
    targetGlobalSpeed.current = Math.max(0.05, Math.min(3.0, targetGlobalSpeed.current + delta));
    if(!showUI) setShowUI(true);
  };

  // Contrôle de vitesse global : touch drag vertical
  const handleTouchStart = (e) => {
    isMouseDown.current = true;
    if (e.touches && e.touches.length > 0) {
      lastTouchY.current = e.touches[0].clientY;
    }
  };

  const handleTouchEnd = () => {
    isMouseDown.current = false;
    lastTouchY.current = null;
  };

  const handleInteraction = (e) => {
    if (e.target.closest('.ui-controls')) return;
    if (e.target.closest('.palette-menu')) return;
    if (e.target.closest('.mandala-menu')) return;

    // Ferme les menus si ouverts
    if (showPaletteMenu) {
      setShowPaletteMenu(false);
      return;
    }
    if (showMandalaMenu) {
      setShowMandalaMenu(false);
      return;
    }

    // Contrôle de vitesse par glissement vertical (touch)
    if (e.touches && e.touches.length > 0 && lastTouchY.current !== null) {
      const currentY = e.touches[0].clientY;
      const deltaY = lastTouchY.current - currentY; // positif = vers le haut = accélérer
      lastTouchY.current = currentY;
      const speedDelta = deltaY * 0.004;
      targetGlobalSpeed.current = Math.max(0.05, Math.min(3.0, targetGlobalSpeed.current + speedDelta));
    }

    // Glow : le doigt dépose de l'encre dans le fluide
    if (mandalaMode === 'glow' && glowSim.current) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      glowSim.current.pointer = { x: clientX, y: clientY };
    }

    // Interaction tactile sur les particules (Morphogenesis et Hyperspace)
    if (mandalaMode === 'morphogenesis' || mandalaMode === 'hyperspace') {
      const particles = mandalaMode === 'hyperspace' ? hyperspaceParticles.current : brushes.current;
      particles.forEach(p => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const dx = clientX - p.x;
        const dy = clientY - p.y;
        if (dx*dx + dy*dy < 20000) {
          p.vx += dx * 0.0001;
          p.vy += dy * 0.0001;
          p.life = Math.max(0.2, p.life - 0.1);
        }
      });
    }
    if(!showUI) setShowUI(true);
  };

  // Styles dynamiques
  const uiTextColor = displayMode === 'white' ? 'text-gray-600' : 'text-gray-400';
  const uiHoverColor = displayMode === 'white' ? 'hover:text-black' : 'hover:text-white';
  const uiPaletteHover = displayMode === 'white' ? 'hover:text-blue-500' : 'hover:text-cyan-400';
  const uiResetHover = displayMode === 'white' ? 'hover:text-purple-500' : 'hover:text-purple-400';
  const uiBg = displayMode === 'white' ? 'bg-white/60 border-white/40' : 'bg-black/60 border-white/10';
  const titleColor = displayMode === 'white' ? 'text-gray-700' : 'text-white';
  const menuBg = displayMode === 'white' ? 'bg-white/90 text-gray-700 shadow-xl' : 'bg-black/90 text-gray-200 border-gray-800 shadow-2xl shadow-white/5';
  const menuItemHover = displayMode === 'white' ? 'hover:bg-gray-100' : 'hover:bg-white/10';

  return (
    <div className={`fixed inset-0 overflow-hidden font-sans select-none touch-none ${displayMode === 'white' ? 'bg-[#fdfdfd]' : 'bg-black'}`}>
      <canvas
        ref={canvasRef}
        className={`block w-full h-full cursor-crosshair ${mandalaMode === 'rosace' ? 'hidden' : ''}`}
        onMouseMove={handleInteraction}
        onMouseDown={() => { isMouseDown.current = true; }}
        onMouseUp={() => { isMouseDown.current = false; }}
        onWheel={handleWheel}
        onTouchMove={handleInteraction}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => { if(!showPaletteMenu && !showMandalaMenu) setShowUI(!showUI); }}
      />
      <canvas
        ref={glCanvasRef}
        className={`absolute inset-0 block w-full h-full cursor-crosshair ${mandalaMode === 'rosace' ? '' : 'hidden'}`}
        onWheel={handleWheel}
        onTouchMove={handleInteraction}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => { if(!showPaletteMenu && !showMandalaMenu) setShowUI(!showUI); }}
      />

      {/* TITRE ET INFO */}
      <div className={`ui-controls absolute bottom-12 left-0 right-0 flex flex-col items-center transition-all duration-1000 ${showUI ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
        
        <div className={`mb-6 flex flex-col items-center gap-1 text-center ${displayMode === 'white' ? 'text-gray-500/80' : 'text-gray-300'}`}>
          <span className="text-[10px] uppercase tracking-[0.3em]">Palette</span>
          <span className={`text-xl font-serif italic animate-pulse duration-[10000ms] ${titleColor} ${displayMode === 'black' ? 'drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]' : ''}`}>{currentPaletteName}</span>
          
          <div className="flex items-center gap-4 mt-3 text-[9px] uppercase tracking-wider opacity-60 font-mono">
            <div className="flex items-center gap-1">
              <Wind size={10}/> <span>{displayMode === 'white' ? 'MORPHOGENÈSE' : 'COSMOS'}</span>
            </div>
            <div className="flex items-center gap-1">
              <MandalaIcon size={10}/> <span>{mandalaMode.toUpperCase()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Grid size={10}/> <span>SYM {dna.current.symmetry}</span>
            </div>
            <div className="flex items-center gap-1">
              {orientation === 'landscape' ? <Monitor size={10}/> : <Smartphone size={10}/>}
              <span>{orientation === 'landscape' ? 'TV' : 'MOBILE'}</span>
            </div>
          </div>
        </div>

        {/* BARRE DE CONTROLE */}
        <div className={`relative flex items-center gap-8 backdrop-blur-xl px-10 py-5 rounded-full border shadow-[0_8px_32px_rgba(0,0,0,0.05)] ${uiBg}`}>
          
          {/* MENU PALETTE (POPUP) */}
          {showPaletteMenu && (
            <div className={`palette-menu absolute bottom-full mb-4 left-1/2 -translate-x-1/2 w-48 rounded-2xl py-2 flex flex-col backdrop-blur-md border border-white/10 z-50 ${menuBg}`}>
              
              <button onClick={selectAutoMode} className={`px-4 py-3 text-xs font-bold uppercase tracking-widest text-left flex items-center gap-2 ${menuItemHover} transition-colors ${paletteMode==='auto'?'text-blue-400':''}`}>
                <InfinityIcon size={14}/> Mode Auto
              </button>
              
              <button onClick={selectFusionMode} className={`px-4 py-3 text-xs font-bold uppercase tracking-widest text-left flex items-center gap-2 ${menuItemHover} transition-colors border-b border-gray-500/10 ${paletteMode==='fusion'?'text-purple-400':''}`}>
                <Layers size={14}/> Fusion Totale
              </button>

              <div className="max-h-48 overflow-y-auto mt-1 custom-scrollbar">
                {getPalettes().map((p, idx) => (
                   <button 
                     key={idx} 
                     onClick={() => selectPalette(idx)}
                     className={`w-full px-4 py-2 text-xs text-left font-serif italic ${menuItemHover} transition-colors ${dna.current.paletteIndex === idx && paletteMode === 'fixed' ? (displayMode==='white'?'text-blue-600 bg-blue-50':'text-cyan-400 bg-cyan-900/20') : ''}`}
                   >
                     {p.name}
                   </button>
                ))}
              </div>
            </div>
          )}

          {/* MENU MANDALA (POPUP) */}
          {showMandalaMenu && (
            <div className={`mandala-menu absolute bottom-full mb-4 left-1/2 -translate-x-1/2 w-48 rounded-2xl py-2 flex flex-col backdrop-blur-md border border-white/10 z-50 ${menuBg}`}>
              <button
                onClick={() => { setMandalaMode('morphogenesis'); setShowMandalaMenu(false); }}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-widest text-left flex items-center gap-2 ${menuItemHover} transition-colors ${mandalaMode === 'morphogenesis' ? (displayMode==='white'?'text-blue-600':'text-cyan-400') : ''}`}
              >
                Morphogenesis
              </button>
              <button
                onClick={() => { setMandalaMode('bubbles'); setShowMandalaMenu(false); }}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-widest text-left flex items-center gap-2 ${menuItemHover} transition-colors ${mandalaMode === 'bubbles' ? (displayMode==='white'?'text-blue-600':'text-cyan-400') : ''}`}
              >
                Bubbles
              </button>
              <button
                onClick={() => { setMandalaMode('hyperspace'); setShowMandalaMenu(false); }}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-widest text-left flex items-center gap-2 ${menuItemHover} transition-colors ${mandalaMode === 'hyperspace' ? (displayMode==='white'?'text-blue-600':'text-cyan-400') : ''}`}
              >
                Hyperspace
              </button>
              <button
                onClick={() => { setMandalaMode('glow'); setShowMandalaMenu(false); }}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-widest text-left flex items-center gap-2 ${menuItemHover} transition-colors ${mandalaMode === 'glow' ? (displayMode==='white'?'text-blue-600':'text-cyan-400') : ''}`}
              >
                Glow
              </button>
              <button
                onClick={() => { setMandalaMode('rosace'); setShowMandalaMenu(false); }}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-widest text-left flex items-center gap-2 ${menuItemHover} transition-colors ${mandalaMode === 'rosace' ? (displayMode==='white'?'text-blue-600':'text-cyan-400') : ''}`}
              >
                Rosace
              </button>
            </div>
          )}

          <button
            onClick={() => { setShowMandalaMenu(!showMandalaMenu); setShowPaletteMenu(false); }}
            className={`${uiTextColor} ${uiHoverColor} transition-colors transform hover:scale-110 ${showMandalaMenu ? (displayMode==='white'?'text-blue-500':'text-cyan-400') : ''}`}
            title="Choisir le Mandala"
          >
            <MandalaIcon size={22} />
          </button>

          <button
            onClick={toggleDisplayMode}
            className={`${uiTextColor} ${uiHoverColor} transition-colors transform hover:scale-110`}
            title={displayMode === 'white' ? "Passer au mode Cosmique" : "Passer au mode Aquarelle"}
          >
            {displayMode === 'white' ? <Moon size={22} strokeWidth={1.5} /> : <Sun size={22} strokeWidth={1.5} />}
          </button>

          <button 
            onClick={() => setIsPlaying(!isPlaying)} 
            className={`${uiTextColor} ${uiHoverColor} transition-colors transform hover:scale-110`}
          >
            {isPlaying ? <Pause size={22} strokeWidth={1.5} /> : <Play size={22} strokeWidth={1.5} />}
          </button>

          <button
            onClick={() => { setShowPaletteMenu(!showPaletteMenu); setShowMandalaMenu(false); }}
            className={`${uiTextColor} ${uiPaletteHover} transition-colors transform hover:scale-110 ${showPaletteMenu ? (displayMode==='white'?'text-blue-500':'text-cyan-400') : ''}`}
            title="Choisir Palette"
          >
            <Palette size={22} strokeWidth={1.5} />
          </button>

          <button 
            onClick={resetCanvas}
            className={`${uiTextColor} ${uiResetHover} transition-colors transform hover:scale-110`}
            title="Nouvelle Toile"
          >
            <Paintbrush size={22} strokeWidth={1.5} />
          </button>
                    {/* BOUTON COEUR */}
                    <button 
            onClick={() => window.open('https://ko-fi.com/lehibou', '_blank')} 
            className={`${uiTextColor} hover:text-red-500 transition-transform hover:scale-125`} 
            title="M'offrir un café"
          >
            ❤️
          </button>

        </div>
        
        <div className="mt-4 text-[9px] text-gray-400 tracking-widest uppercase opacity-50">
          Glissez ↕ ou molette pour la vitesse • Tapotez pour cacher
        </div>

      </div>

      <div className={`ui-controls absolute top-6 right-6 transition-opacity duration-1000 ${showUI ? 'opacity-40 hover:opacity-100' : 'opacity-0'}`}>
        <button onClick={() => {
          const el = document.documentElement;
          if(document.fullscreenElement) document.exitFullscreen();
          else el.requestFullscreen().catch(()=>{});
        }} className={`${displayMode === 'white' ? 'text-gray-500' : 'text-gray-400'} p-2`}>
          <Maximize size={20} strokeWidth={1.5} />
        </button>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(150, 150, 150, 0.2); border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(150, 150, 150, 0.4); }
      `}</style>

    </div>
  );
};

export default App;

